import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import MyPageBreadcrumb from '../../components/mypage/MyPageBreadcrumb';
import CertificateModal from '../../components/modals/CertificateModal';
import { getMyClassroomLectures, searchMyClassroomLectures, enrollLecture } from '../../services/mypageApi';

interface MyPageClassroomProps {}

interface LectureData {
  lecture_info: {
    id: number;
    title: string;
    caption: string;
    thumbnail_url: string;
    tags: string[];
  };
  lecturer_info: {
    id: number;
    name: string;
    department?: string;
    introduction?: string;
    image_url: string;
    tags: string[];
  };
  status: string;
  completion_rate: number;
}

const MyPageClassroom: React.FC<MyPageClassroomProps> = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'ongoing' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'completion' | 'certificate'>('completion');
  
  // API 상태 관리
  const [lectureData, setLectureData] = useState<LectureData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // 강의 데이터 로드
  const loadLectures = useCallback(async (isDone?: boolean, reset: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const page = reset ? 0 : currentPage;
      /// console.log('마이 페이지 강의 데이터 API 요청:', { isDone, page, reset });
      
      const response = await getMyClassroomLectures(isDone, page, 20); // 한 번에 더 많이 로드
      /// console.log('마이 페이지 강의 데이터 API 응답:', { response });
      
      if (reset) {
        setLectureData(response.data.content);
        setCurrentPage(0);
      } else {
        setLectureData(prev => [...prev, ...response.data.content]);
      }
      
      setTotalPages(response.data.totalPages);
      setHasMore(page + 1 < response.data.totalPages);
      setCurrentPage(page + 1);
      
      // /// console.log('강의 데이터 로드 완료:', {
      //   loaded: response.data.content.length,
      //   total: response.data.totalElements,
      //   hasMore: page + 1 < response.data.totalPages
      // });
      
    } catch (error) {
      /// console.error('강의 데이터 로드 실패:', error);
      setError('강의 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  // 검색 함수 - 페이징된 API 응답 구조 처리
  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      // 검색어가 없으면 전체 목록 다시 로드
      loadLectures(getIsDoneFilter(), true);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      /// console.log('마이 페이지 클래스룸 강의 검색 API 요청:', {keyword});
      const response = await searchMyClassroomLectures(keyword);
      /// console.log('마이 페이지 클래스룸 강의 검색 API 응답:', {response});
      
      // 페이징된 응답 구조인지 확인하고 처리
      if (response.data && Array.isArray(response.data.content)) {
        // 페이징된 응답 구조
        setLectureData(response.data.content);
      } else if (Array.isArray(response.data)) {
        // 기존 배열 응답 구조
        setLectureData(response.data);
      } else {
        // 예상과 다른 응답 구조
        /// console.error('예상과 다른 검색 응답 구조:', response.data);
        setLectureData([]);
        setError('검색 결과를 처리할 수 없습니다.');
      }
      
      setHasMore(false); // 검색 결과는 페이징 없음
      
      // /// console.log('검색 완료:', Array.isArray(response.data.content) ? response.data.content.length : (Array.isArray(response.data) ? response.data.length : 0), '개 결과');
    } catch (error) {
      /// console.error('검색 실패:', error);
      setError('검색에 실패했습니다.');
      setLectureData([]); // 오류 발생 시 빈 배열로 설정
    } finally {
      setIsLoading(false);
    }
  }, [loadLectures]);

  // 탭에 따른 isDone 필터 값 계산
  const getIsDoneFilter = () => {
    if (activeTab === 'ongoing') return false;
    if (activeTab === 'completed') return true;
    return undefined; // all
  };

  // 초기 로드 및 탭 변경 시 로드
  useEffect(() => {
    if (!searchQuery.trim()) {
      loadLectures(getIsDoneFilter(), true);
    }
  }, [activeTab]); // searchQuery 제외

  // 검색어 변경 시 디바운스된 검색
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // 더미 데이터 제거하고 API 데이터 사용 - 배열 안전성 확보
  const filteredLectures = Array.isArray(lectureData) ? lectureData : [];

  // 상태와 수강률에 따른 진도 아이콘 결정
  const getProgressIcon = (status: string, completionRate: number) => {
    // 수강 완료 상태들 또는 100%
    if (['ENDED', 'INTERVIEWED', 'ISSUED'].includes(status) || completionRate >= 100) return 'circleCheck';
    // 진행 중 (50~99%)
    if (completionRate >= 50) return 'fire';
    // 시작 단계 (0~50%)
    return 'history';
  };

  // 상태와 수강률에 따른 버튼 텍스트 및 아이콘 결정
  const getButtonInfo = (status: string, completionRate: number) => {
    // 수강률 100%인데 아직 설문조사를 하지 않은 경우
    if (completionRate >= 100 && !['INTERVIEWED', 'ISSUED'].includes(status)) {
      return { text: '강의 설문하기', icon: 'survey' as const };
    }
    
    switch (status) {
      case 'ENDED':
      case 'INTERVIEWED':
      case 'ISSUED':
        return { text: '강의 다시보기', icon: 'play' as const };
      case 'IN_PROGRESS':
      case 'ENROLLED':
        return { text: '강의 시청하기', icon: 'play' as const };
      default:
        return { text: '강의 시청하기', icon: 'play' as const };
    }
  };

  // 상태 한글 변환
  const getStatusText = (status: string) => {
    switch (status) {
      case 'INTERVIEWED':  // 설문조사 완료한 경우만 수강 완료
      case 'ISSUED':       // 이수증 발급된 경우만 수강 완료
        return '수강 완료';
      case 'ENDED':        // 강의 종료되었지만 설문 미완료
      case 'IN_PROGRESS':
      case 'ENROLLED':
      default:
        return '수강 중';
    }
  };

  // 강의 스트리밍 클릭 핸들러
  const handleLectureStreamingClick = async (lectureId: number) => {
    try {
      /// console.log('강의 시청 버튼 클릭 요청 수강신청 (ID):', lectureId);

      // 수강 신청 API 호출
      const result = await enrollLecture(lectureId);
      /// console.log('강의 시청 버튼 클릭 요청 수강시청 API 응답:', {result});
      
      // 성공 또는 이미 수강신청된 경우 스트리밍 창 열기
      if (result.code === 200 || result.code === 201 || result.code === 409) {
        /// console.log('수강 신청 처리 완료. 스트리밍 창을 엽니다.');
        openStreamingWindow(lectureId);
      } else {
        /// console.log('수강 신청 실패:', result.message);
        const errorMessage = result.message || '알 수 없는 오류가 발생했습니다';
        alert(`수강 신청에 실패했습니다: ${errorMessage}`);
      }
    } catch (error: any) {
      /// console.error('강의 시청 처리 중 오류:', error);
      
      if (error.message === 'AUTH_EXPIRED') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else if (error.message?.startsWith('SERVER_ERROR:')) {
        const errorMessage = error.message.replace('SERVER_ERROR: ', '');
        alert(`서버 오류: ${errorMessage}`);
      } else {
        alert('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 강의 설문 클릭 핸들러
  const handleSurveyClick = (lectureId: number, lecture_title?: string) => {
    const encodedTitle = lecture_title ? encodeURIComponent(lecture_title) : '';
    window.open(`/mypage/survey/${lectureId}?lecture_title=${encodedTitle}`, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };

  // 스트리밍 창 열기 함수
  const openStreamingWindow = (lectureId: number) => {
    /// console.log('=== 스트리밍 창 열기 시작 ===');
    /// console.log('강의 ID:', lectureId);
    
    // 새 창으로 스트리밍 페이지 띄우기
    const streamingUrl = `/class/streaming/${lectureId}`;
    /// console.log('스트리밍 URL:', streamingUrl);
    
    const newWindow = window.open(streamingUrl, '_blank', 'width=1440,height=900,scrollbars=yes,resizable=yes');
    
    if (newWindow) {
      /// console.log('스트리밍 창이 성공적으로 열렸습니다.');
    } else {
      /// console.error('스트리밍 창을 열 수 없습니다. 팝업이 차단되었을 수 있습니다.');
      alert('새 창을 열 수 없습니다. 팝업 차단을 해제해주세요.');
    }
  };

  // 강의 상세 페이지로 이동
  const handleLectureDetailClick = (lectureId: number) => {
    navigate(`/class/lecture/${lectureId}`);
  };

  const handleIssueCompletion = () => {
    setModalType('completion');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderIcon = (iconName: 'play' | 'fire' | 'history' | 'circleCheck' | 'bulb' | 'clipboard' | 'survey', color: string = '#448181') => {
    if (iconName === 'survey') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d="M10.0002 1.75C6.59441 1.75 3.8335 4.51091 3.8335 7.91667C3.8335 10.2174 5.09351 12.2226 6.9585 13.2821L6.9585 13.4444C6.95848 13.7707 6.95847 14.0594 6.97802 14.2987C6.99884 14.5536 7.04551 14.8176 7.17648 15.0746C7.36823 15.451 7.67419 15.7569 8.05052 15.9487C8.30756 16.0797 8.5716 16.1263 8.82642 16.1471C9.06571 16.1667 9.35444 16.1667 9.68068 16.1667H10.3196C10.6458 16.1667 10.9346 16.1667 11.1739 16.1471C11.4287 16.1263 11.6928 16.0797 11.9498 15.9487C12.3261 15.7569 12.6321 15.451 12.8238 15.0746C12.9548 14.8176 13.0015 14.5536 13.0223 14.2987C13.0419 14.0595 13.0418 13.7708 13.0418 13.4445L13.0418 13.2821C14.9068 12.2226 16.1668 10.2174 16.1668 7.91667C16.1668 4.51091 13.4059 1.75 10.0002 1.75ZM5.33354 7.9165C5.33354 5.33917 7.42287 3.24984 10.0002 3.24984C12.5775 3.24984 14.6669 5.33917 14.6669 7.9165C14.6669 9.78704 13.5663 11.4021 11.9742 12.1465C11.7104 12.2699 11.5419 12.5347 11.5419 12.8259V13.6665C11.5419 14.1902 11.5326 14.2742 11.5174 14.321C11.4679 14.4732 11.3486 14.5926 11.1964 14.642C11.1495 14.6573 11.0656 14.6665 10.5419 14.6665H9.45854C8.93484 14.6665 8.85087 14.6573 8.80403 14.642C8.65181 14.5926 8.53247 14.4732 8.48301 14.321C8.46779 14.2742 8.45854 14.1902 8.45854 13.6665V12.8259C8.45854 12.5347 8.28999 12.2699 8.02622 12.1465C6.43406 11.4021 5.33354 9.78704 5.33354 7.9165Z" fill={color}/>
          <path d="M8.12518 17.5837C7.71097 17.5837 7.37518 17.9194 7.37518 18.3337C7.37518 18.7479 7.71097 19.0837 8.12518 19.0837H11.8752C12.2894 19.0837 12.6252 18.7479 12.6252 18.3337C12.6252 17.9194 12.2894 17.5837 11.8752 17.5837H8.12518Z" fill={color}/>
        </svg>
      );
    }
    
    if (iconName === 'clipboard') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.6667 2.66667H12C12.3536 2.66667 12.6928 2.80714 12.9428 3.05719C13.1929 3.30724 13.3333 3.64638 13.3333 4V13.3333C13.3333 13.687 13.1929 14.0261 12.9428 14.2761C12.6928 14.5262 12.3536 14.6667 12 14.6667H4C3.64638 14.6667 3.30724 14.5262 3.05719 14.2761C2.80714 14.0261 2.66667 13.687 2.66667 13.3333V4C2.66667 3.64638 2.80714 3.30724 3.05719 3.05719C3.30724 2.80714 3.64638 2.66667 4 2.66667H5.33333" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 1.33333H6C5.63181 1.33333 5.33333 1.63181 5.33333 2V3.33333C5.33333 3.70152 5.63181 4 6 4H10C10.3682 4 10.6667 3.70152 10.6667 3.33333V2C10.6667 1.63181 10.3682 1.33333 10 1.33333Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5.33333 7.33333L6.66667 8.66667L10.6667 4.66667" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    if (iconName === 'fire') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.66633 10.0678C5.66633 11.3344 6.69966 12.3344 7.99966 12.3344C9.29965 12.3344 10.333 11.3344 10.333 10.0678C10.333 8.80112 9.29965 7.80112 7.99966 6.66778C6.69966 7.80112 5.66633 8.80112 5.66633 10.0678Z" fill={color}/>
          <path fillRule="evenodd" clipRule="evenodd" d="M9.50951 1.22215C9.00344 0.756373 8.20714 0.871869 7.84433 1.44329L5.87352 4.54733L5.40713 4.12758C4.99188 3.75385 4.32508 3.73469 3.90627 4.17603C2.6406 5.50981 1.89966 7.25101 1.89966 9.06806C1.89966 10.7547 2.52587 12.2308 3.62133 13.2839C4.71538 14.3357 6.2446 14.9347 7.99964 14.9347C11.552 14.9347 14.0996 12.345 14.0996 9.06806C14.0996 6.29289 12.3268 3.81509 9.50951 1.22215ZM6.50617 5.78967L8.79059 2.19169C11.5056 4.70975 12.8996 6.85725 12.8996 9.06806C12.8996 11.6578 10.9139 13.7347 7.99964 13.7347C6.52136 13.7347 5.30058 13.2337 4.45298 12.4189C3.60677 11.6053 3.09965 10.4481 3.09965 9.06806C3.09965 7.62148 3.67392 6.20941 4.68933 5.09599L5.59827 5.91404C5.72952 6.03216 5.90613 6.08647 6.08108 6.06251C6.25602 6.03855 6.41153 5.93874 6.50617 5.78967Z" fill={color}/>
        </svg>
      );
    }
    
    if (iconName === 'history') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.99988 1.40039C7.66851 1.40039 7.39988 1.66902 7.39988 2.00039C7.39988 2.33176 7.66851 2.60039 7.99988 2.60039C10.9822 2.60039 13.3999 5.01805 13.3999 8.00039C13.3999 10.9827 10.9822 13.4004 7.99988 13.4004C5.01755 13.4004 2.5999 10.9827 2.5999 8.00039C2.5999 7.66902 2.33127 7.40039 1.9999 7.40039C1.66853 7.40039 1.3999 7.66902 1.3999 8.00039C1.3999 11.6455 4.35481 14.6004 7.99988 14.6004C11.6449 14.6004 14.5999 11.6455 14.5999 8.00039C14.5999 4.35531 11.6449 1.40039 7.99988 1.40039Z" fill={color}/>
          <path d="M6.31897 2.20027C6.46 2.54038 6.29863 2.93042 5.95852 3.07146C5.61842 3.2125 5.22837 3.05112 5.08733 2.71102C4.9463 2.37091 5.10767 1.98087 5.44778 1.83983C5.78788 1.69879 6.17793 1.86017 6.31897 2.20027Z" fill={color}/>
          <path d="M4.22865 3.28631C4.489 3.54666 4.489 3.96877 4.22865 4.22912C3.9683 4.48947 3.54619 4.48948 3.28584 4.22913C3.02549 3.96878 3.02549 3.54667 3.28584 3.28632C3.54619 3.02597 3.9683 3.02596 4.22865 3.28631Z" fill={color}/>
          <path d="M2.71054 5.08781C3.05065 5.22885 3.21202 5.61889 3.07099 5.959C2.92995 6.29911 2.53991 6.46049 2.1998 6.31945C1.8597 6.17842 1.69832 5.78837 1.83936 5.44827C1.98039 5.10816 2.37044 4.94678 2.71054 5.08781Z" fill={color}/>
          <path d="M7.66654 4.40039C7.99791 4.40039 8.26654 4.66902 8.26654 5.00039V8.08528L9.74063 9.55938C9.97495 9.79369 9.97495 10.1736 9.74063 10.4079C9.50632 10.6422 9.12642 10.6422 8.89211 10.4079L7.2422 8.75799C7.12251 8.6383 7.06396 8.48063 7.06655 8.32378V5.00039C7.06655 4.66902 7.33518 4.40039 7.66654 4.40039Z" fill={color}/>
        </svg>
      );
    }
    
    if (iconName === 'circleCheck') {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.0977 6.58435C11.3281 6.34625 11.3219 5.9664 11.0838 5.73593C10.8457 5.50547 10.4659 5.51166 10.2354 5.74977L7.11811 8.97041L5.76491 7.56853C5.53477 7.33012 5.15493 7.32341 4.91652 7.55355C4.6781 7.78369 4.67139 8.16353 4.90153 8.40194L6.68584 10.2504C6.79881 10.3675 6.95446 10.4336 7.11712 10.4337C7.27978 10.4338 7.43552 10.3679 7.54865 10.251L11.0977 6.58435Z" fill={color}/>
          <path fillRule="evenodd" clipRule="evenodd" d="M7.99988 1.40039C4.35481 1.40039 1.3999 4.35531 1.3999 8.00039C1.3999 11.6455 4.35481 14.6004 7.99988 14.6004C11.6449 14.6004 14.5999 11.6455 14.5999 8.00039C14.5999 4.35531 11.6449 1.40039 7.99988 1.40039ZM2.5999 8.00039C2.5999 5.01805 5.01755 2.60039 7.99988 2.60039C10.9822 2.60039 13.3999 5.01805 13.3999 8.00039C13.3999 10.9827 10.9822 13.4004 7.99988 13.4004C5.01755 13.4004 2.5999 10.9827 2.5999 8.00039Z" fill={color}/>
        </svg>
      );
    }
    
    if (iconName === 'play') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.02809 15.525C7.70871 16.3084 7.04901 16.7001 6.50662 16.6478C6.03364 16.6021 5.60261 16.3568 5.3219 15.9734C5 15.5337 5 14.7665 5 13.2321V6.76799C5 5.23356 5 4.46635 5.3219 4.02668C5.60261 3.64327 6.03364 3.39792 6.50662 3.35229C7.04901 3.29997 7.7087 3.69166 9.02809 4.47505L14.4715 7.70708C15.7435 8.46234 16.3795 8.83997 16.5946 9.32958C16.7824 9.75683 16.7824 10.2432 16.5946 10.6705C16.3795 11.1601 15.7435 11.5377 14.4715 12.293L9.02809 15.525Z" fill={color}/>
        </svg>
      );
    }
    
    if (iconName === 'bulb') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9,21C9,22.1 9.9,23 11,23H13C14.1,23 15,22.1 15,21V20H9V21M12,2C8.14,2 5,5.14 5,9C5,11.38 6.19,13.47 8,14.74V17C8,17.55 8.45,18 9,18H15C15.55,18 16,17.55 16,17V14.74C17.81,13.47 19,11.38 19,9C19,5.14 15.86,2 12,2Z" fill={color} />
        </svg>
      );
    }
    
    // Return empty if icon not found
    return null;
  };

  return (
    <MyPageLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>나의 강의실</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색바 */}
          <NavigationBar>
            <MyPageBreadcrumb 
              items={[
                { label: '홈' },
                { label: '마이페이지' },
                { label: '나의 강의실' }
              ]}
            />
            
            {/* 검색 */}
            <SearchContainer>
              <SearchInput
                placeholder="검색어를 입력해주세요."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SearchIcon>
            </SearchContainer>
          </NavigationBar>

          {/* 나의 강의실 콘텐츠 */}
          <Section>

            {/* 안내 메시지 */}
            <InfoSection>
              <InfoList>
                <InfoItem>강의 영상을 모두 시청하신 후 진도율이 100%에 도달했을 때 강의 설문을 하실 수 있습니다.</InfoItem>
                <InfoItem>강의 진도율이 100%에 도달하고 강의 설문이 완료되면 강의가 '수강 완료' 상태로 전환됩니다.</InfoItem>
                <InfoItem>'수강 완료' 상태인 강의에 대하여 '수강 완료' 탭에서 '이수 내역'을 발급하실 수 있습니다.</InfoItem>
              </InfoList>
            </InfoSection>

            {/* 탭 및 버튼 영역 */}
            <TabSection>
              <TabContainer>
                <Tab 
                  active={activeTab === 'all'} 
                  onClick={() => setActiveTab('all')}
                >
                  전체보기
                </Tab>
                <Tab 
                  active={activeTab === 'ongoing'} 
                  onClick={() => setActiveTab('ongoing')}
                >
                  수강 중
                </Tab>
                <Tab 
                  active={activeTab === 'completed'} 
                  onClick={() => setActiveTab('completed')}
                >
                  수강 완료
                </Tab>
              </TabContainer>
              
              {activeTab === 'completed' && (
                <IssueButton onClick={handleIssueCompletion}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C17.5 2 22 6.5 22 12S17.5 22 12 22 2 17.5 2 12 6.5 2 12 2M10 17L18 9L16.59 7.58L10 14.17L7.41 11.59L6 13L10 17Z" fill="white"/>
                  </svg>
                  이수 내역 발급하기
                </IssueButton>
              )}
            </TabSection>

            {/* 강의 목록 */}
            <LectureList>
              {isLoading && lectureData.length === 0 ? (
                <LoadingMessage>강의 목록을 불러오는 중...</LoadingMessage>
              ) : error ? (
                <ErrorMessage>{error}</ErrorMessage>
              ) : filteredLectures.length === 0 ? (
                <EmptyMessage>
                  {searchQuery ? '검색 결과가 없습니다.' : '수강 중인 강의가 없습니다.'}
                </EmptyMessage>
              ) : (
                <>
                  {filteredLectures.map((lecture) => {
                    const completionRate = lecture.completion_rate || 0;
                    const buttonInfo = getButtonInfo(lecture.status, completionRate);
                    const isCompletedStatus = ['INTERVIEWED', 'ISSUED'].includes(lecture.status);
                    const shouldShowCompleted = isCompletedStatus;
                    const isSurveyNeeded = completionRate >= 100 && !['INTERVIEWED', 'ISSUED'].includes(lecture.status);
                    
                    return (
                      <LectureCard key={lecture.lecture_info.id}>
                        <StatusContainer>
                          {/* 수강 완료 상태 (100%) */}
                          {shouldShowCompleted ? (
                            <StatusBadge status={lecture.status}>
                              {getStatusText(lecture.status)}
                            </StatusBadge>
                          ) : (
                            <>
                              <StatusBadge status={lecture.status}>
                                {getStatusText(lecture.status)}
                              </StatusBadge>
                              {/* 진행률 표시 */}
                              {!['INTERVIEWED', 'ISSUED'].includes(lecture.status) && (
                                <ProgressBadge>
                                  {renderIcon(getProgressIcon(lecture.status, completionRate))}
                                  {Math.floor(completionRate * 100) / 100}%
                                </ProgressBadge>
                              )}
                            </>
                          )}
                        </StatusContainer>
                        
                        <LectureInfo>
                          <LectureTitle>{lecture.lecture_info.title}</LectureTitle>
                          <LectureInstructor>
                            {lecture.lecturer_info.name}
                            {lecture.lecturer_info.department && ` · ${lecture.lecturer_info.department}`}
                          </LectureInstructor>
                        </LectureInfo>
                        
                        <LectureFooter>
                          <LectureLink onClick={() => handleLectureDetailClick(lecture.lecture_info.id)}>
                            강의 바로가기
                            <svg width="6" height="12" viewBox="0 0 6 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.43185 2.43258C1.25611 2.60832 1.25611 2.89324 1.43185 3.06898L4.36365 6.00078L1.43185 8.93258C1.25612 9.10832 1.25612 9.39324 1.43185 9.56898C1.60759 9.74471 1.89251 9.74471 2.06825 9.56898L5.31825 6.31898C5.49398 6.14324 5.49398 5.85832 5.31825 5.68258L2.06825 2.43258C1.89251 2.25685 1.60759 2.25685 1.43185 2.43258Z" fill="#37383C" fillOpacity="0.61"/>
                            </svg>
                          </LectureLink>
                          
                          <ActionButton 
                            variant={['INTERVIEWED', 'ISSUED'].includes(lecture.status) ? 'secondary' : 'primary'}
                            onClick={() => {
                              if (isSurveyNeeded) {
                                handleSurveyClick(lecture.lecture_info.id, lecture.lecture_info.title);
                              } else {
                                handleLectureStreamingClick(lecture.lecture_info.id);
                              }
                            }}
                          >
                            {renderIcon(buttonInfo.icon, ['INTERVIEWED', 'ISSUED'].includes(lecture.status) ? 'rgba(46, 47, 51, 0.88)' : 'white')}
                            {buttonInfo.text}
                          </ActionButton>
                        </LectureFooter>
                      </LectureCard>
                    );
                  })}
                  
                  {/* 더 보기 버튼 */}
                  {hasMore && !searchQuery && (
                    <LoadMoreButton onClick={() => loadLectures(getIsDoneFilter(), false)}>
                      {isLoading ? '로딩 중...' : '더 보기'}
                    </LoadMoreButton>
                  )}
                </>
              )}
            </LectureList>
          </Section>
        </MainContainer>
      </Container>
      
      {/* 모달 */}
      <CertificateModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        type={modalType}
      />
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationBar = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 282px;
  height: 32px; /* 브레드크럼과 같은 높이 설정 */
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 8px 32px 8px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: #171719;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  height: 16px;
  padding-left: 2px;
  padding-right: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const InfoSection = styled.div`
  width: 100%;
  padding: 32px;
  background: rgba(112, 115, 124, 0.08);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

const InfoList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.li`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  position: relative;
  padding-left: 16px;
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #171719;
    font-weight: bold;
  }
`;

const TabSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 7px 11px;
  border-radius: 10px;
  border: ${props => props.active ? 'none' : '1px solid rgba(112, 115, 124, 0.16)'};
  background: ${props => props.active ? 'black' : 'transparent'};
  color: ${props => props.active ? '#F7F7F8' : 'rgba(55, 56, 60, 0.61)'};
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const IssueButton = styled.button`
  padding: 9px 16px;
  background: #448181;
  border-radius: 10px;
  border: none;
  color: white;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.14px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const LectureList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LectureCard = styled.div`
  width: 100%;
  padding: 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 4px 8px;
  border-radius: 8px;
  background: ${props => {
    if (['ENDED', 'INTERVIEWED', 'ISSUED'].includes(props.status)) return 'rgba(112, 115, 124, 0.08)';
    if (props.status === 'IN_PROGRESS') return 'rgba(68, 129, 129, 0.08)';
    return 'rgba(68, 129, 129, 0.08)';
  }};
  color: ${props => {
    if (['ENDED', 'INTERVIEWED', 'ISSUED'].includes(props.status)) return 'rgba(55, 56, 60, 0.61)';
    if (props.status === 'IN_PROGRESS') return '#448181';
    return '#448181';
  }};
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const LoadingMessage = styled.div`
  width: 100%;
  padding: 60px 20px;
  text-align: center;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: 60px 20px;
  text-align: center;
  /* color: #ff4757; */
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const EmptyMessage = styled.div`
  width: 100%;
  padding: 60px 20px;
  text-align: center;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  background: transparent;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(112, 115, 124, 0.04);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressBadge = styled.div`
  padding: 4px 8px;
  border-radius: 8px;
  background: rgba(68, 129, 129, 0.08);
  color: #448181;
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LectureInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LectureTitle = styled.h3`
  color: black;
  font-size: 22px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 30px;
  margin: 0;
`;

const LectureInstructor = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const LectureFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 48px;
`;

const LectureLink = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  background: ${props => props.variant === 'primary' ? '#448181' : 'rgba(112, 115, 124, 0.08)'};
  color: ${props => props.variant === 'primary' ? 'white' : 'rgba(46, 47, 51, 0.88)'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: ${props => props.variant === 'primary' ? '600' : '500'};
  line-height: 24px;
  letter-spacing: 0.09px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.variant === 'secondary' && `
    backdrop-filter: blur(32px);
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(112, 115, 124, 0.08);
      z-index: -1;
    }
  `}
  
  &:hover {
    opacity: 0.8;
  }
`;

export default MyPageClassroom;